import { Box, Stack, Tab, useMediaQuery, useTheme } from '@mui/material';
import type { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { useFieldById, useMyFields, useMyFieldsInfinite } from '/@/hooks/fields';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import type { CropType } from '/@/types/crop-monitoring';
import { getNodeScopeFilters, type NodeScopeFilters } from '/@/utils/node-scope';
import Tabs from '../new-tabs';
import FieldListV2FormFilters from './components/filters';
import FieldListV2Map from './components/map';
import FieldListV2Modals from './components/modals';
import FieldListV2Sidebar from './components/sidebar';

export type FieldListV2FormType = {
    fieldId: number | null;
    name: string | null;
    producerId: number | null;
    crop: CropType | 'fallow' | null;
    plantingDateFrom: Dayjs | null;
    plantingDateTo: Dayjs | null;
};

const useFieldListV2FormAndQueries = (userNodeId: number, nodeScope: NodeScopeFilters) => {
    // Form & Query Params
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedFieldIdQueryParam = searchParams.get('fieldId');
    const form = useForm<FieldListV2FormType>({
        defaultValues: {
            fieldId: selectedFieldIdQueryParam ? Number(selectedFieldIdQueryParam) : null,
            name: null,
            producerId: null,
            crop: null,
            plantingDateFrom: null,
            plantingDateTo: null,
        },
    });
    const fieldId = form.watch('fieldId');
    useEffect(() => {
        if (fieldId) {
            setSearchParams({ ...searchParams, fieldId: fieldId.toString() }, { replace: true });
        } else {
            searchParams.delete('fieldId');
            setSearchParams(searchParams, { replace: true });
        }
    }, [fieldId]);

    const name = form.watch('name');
    const debouncedName = useDebounce(name, 500);
    const producerId = form.watch('producerId');
    const crop = form.watch('crop');
    const plantingDateFrom = form.watch('plantingDateFrom');
    const plantingDateTo = form.watch('plantingDateTo');
    useEffect(() => {
        if (debouncedName || producerId || crop || plantingDateFrom || plantingDateTo) form.setValue('fieldId', null);
    }, [debouncedName, producerId, crop, plantingDateFrom, plantingDateTo, form.setValue]);

    const country = useSelector(getSessionCountry);

    const fieldsQueryFilterArgs = useMemo(
        () => ({
            name: debouncedName && debouncedName !== '' ? debouncedName : undefined,
            userNodeId: producerId || userNodeId,
            nodeType: nodeScope.nodeType,
            includeChildren: nodeScope.includeChildren && !producerId,
            cmPlantingDateFrom: plantingDateFrom?.toISOString().split('T')[0] || undefined,
            cmPlantingDateTo: plantingDateTo?.toISOString().split('T')[0] || undefined,
            activeCrop: crop || undefined,
            excludeTargetNode: nodeScope.excludeTargetNode,
        }),
        [
            debouncedName,
            producerId,
            userNodeId,
            plantingDateFrom,
            plantingDateTo,
            crop,
            nodeScope.includeChildren,
            nodeScope.excludeTargetNode,
            nodeScope.nodeType,
        ]
    );

    const fieldsQuery = useMyFieldsInfinite({
        country: country.code!,
        ...fieldsQueryFilterArgs,
    });
    const allFieldsQuery = useMyFields({
        page: 1,
        pageSize: 10000,
        country: country.code,
        selectOnly: 'id,lat,lng,name,location,altitude,area,geometry,externalProvider',
        ...fieldsQueryFilterArgs,
    });
    const fieldQuery = useFieldById({ id: fieldId! }, { enabled: !!fieldId });

    return { form, fieldsQuery, allFieldsQuery, fieldQuery };
};

const FieldListV2 = ({
    userNodeId,
    nodeScope = getNodeScopeFilters('myFields'),
    emptyMessage,
}: {
    userNodeId: number;
    nodeScope?: NodeScopeFilters;
    emptyMessage: string;
}) => {
    const { form, fieldsQuery, allFieldsQuery, fieldQuery } = useFieldListV2FormAndQueries(userNodeId, nodeScope);
    const [tab, setTab] = useState<'fields' | 'map'>('fields');

    const theme = useTheme();
    const isMdOrLower = useMediaQuery(theme.breakpoints.down('md'));

    const showMap = (isMdOrLower && tab === 'map') || !isMdOrLower;
    const showFields = (isMdOrLower && tab === 'fields') || !isMdOrLower;

    return (
        <Stack spacing={3}>
            <FieldListV2FormFilters
                form={form}
                userNodeId={userNodeId}
                includeChildrenNodes={nodeScope.includeChildren}
            />
            <Box>
                {isMdOrLower && (
                    <Tabs value={tab} onChange={(_, value: 'fields' | 'map') => setTab(value)}>
                        <Tab label={_('Lotes')} value='fields' />
                        <Tab label={_('Mapa')} value='map' />
                    </Tabs>
                )}
                <Stack direction='row' sx={{ height: '650px' }}>
                    {showFields && (
                        <Box width={{ md: 500, xs: '100%' }} sx={{ overflow: 'auto' }}>
                            <FieldListV2Sidebar
                                form={form}
                                fieldsQuery={fieldsQuery}
                                isRVView={nodeScope.includeChildren}
                                userNodeId={userNodeId}
                                emptyMessage={emptyMessage}
                            />
                        </Box>
                    )}
                    {showMap && (
                        <Box flex={1} sx={{ position: 'relative' }}>
                            <FieldListV2Map allFieldsQuery={allFieldsQuery} fieldQuery={fieldQuery} form={form} />
                        </Box>
                    )}
                </Stack>
            </Box>

            <FieldListV2Modals />
        </Stack>
    );
};

export default FieldListV2;
